import React, { useEffect, useState } from "react";

import { Box, Drawer, Typography } from "@mui/material";

import { Form } from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import yaml from 'js-yaml';
import { JSONSchema7 } from "json-schema";

import { axiosRepoIntance, axiosWorkflowIntance, } from "../../services/api-client";
import { Package } from "../../services/repo-service";
import { base64ToUtf8, OWNER, utf8ToBase64 } from "../../utils/constants/gitea";
import { initializedSecrets } from "../secrets-and-variables/SecretsList";

interface WorkflowStep {
  name: string;
  env?: Record<string, string>;
}

interface Workflow {
  jobs: {
    deploy: {
      steps: WorkflowStep[];
    };
  };
}

interface AddDeploymentProps {
  open: boolean;
  onClose: () => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormDataType {
  name?: string;
  version?: string;
  namespace?: string;
  resourceLimits?: {
    cpu?: number;
    memory?: number;
  };
  resourceRequest?: {
    cpu?: number;
    memory?: number;
  };
  environment?: {
    secrets?: string[];
    variables?: string[];
  };
}

const uiSchema = {
  secrets: {
    "ui:widget": "checkboxes",
    "ui:options": {
      enumDisabled: [...initializedSecrets],
    },
  },
  variables: {
    "ui:widget": "checkboxes",
  },
};

const AddDeployment: React.FC<AddDeploymentProps> = ({ open, onClose, setRefreshKey, setIsLoading }) => {
  const [formData, setFormData] = useState<FormDataType>({ environment: { secrets: [...initializedSecrets] } });
  const [nameToVersionMap, setNameToVersionMap] = useState<Record<string, string[]>>({});
  const [versionMap, setVersionMap] = useState<Record<string, string>>({});
  const [namespacesData, setNamespacesData] = useState<string[]>([]);
  const [variables, setVariables] = useState<string[]>([]);
  const [secrets, setSecrets] = useState<string[]>([]);

  const [schema, setSchema] = useState<JSONSchema7>({
    type: "object",
    properties: {
      name: { type: "string", title: "Name", enum: [] },
      version: { type: "string", title: "Version", enum: [] },
      namespace: { type: "string", title: "Namespace", enum: [] },
      resourceLimits: {
        type: "object",
        title: "Resource Limits",
        properties: {
          cpu: {
            type: "number",
            title: "CPU Limit(in m)",
            minimum: 1,
          },
          memory: {
            type: "number",
            title: "Memory Limit(in Mi)",
            minimum: 1,
          },
        },
      },
      resourceRequest: {
        type: "object",
        title: "Resource Request",
        properties: {
          cpu: {
            type: "number",
            title: "CPU Limit(in m)",
            minimum: 1,
          },
          memory: {
            type: "number",
            title: "Memory Limit(in Mi)",
            minimum: 1,
          },
        },
      },
      secrets: {
        type: "array",
        title: "Secrets",
        items: { type: "string", enum: [] },
        uniqueItems: true,
      },
      variables: {
        type: "array",
        title: "Variables",
        items: { type: "string", enum: [] },
        uniqueItems: true,
      },
    },
    required: ["name", "version", "namespace"],
  });

  const extractNumericValue = (value: string): number => {
    const match = value.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  };

  const listRepoActionSecrets = async () => {
    try {
      const response = await axiosRepoIntance.orgs.orgListActionsSecrets(OWNER);
      if (response.data && response.data.length > 0) {
        const secretNames = response.data.map((secret) => secret.name).filter((name): name is string => !!name);
        setSecrets(secretNames);
      } else {
        // console.log("No secrets found.");
        setSecrets([]);
      }
    } catch (error) {
      console.error("Error while fetching secrets list:", error);
    }
  };

  const listRepoActionVariables = async () => {
    try {
      const response = await axiosRepoIntance.orgs.getOrgVariablesList(OWNER);
      if (response.data && response.data.length > 0) {
        const variableNames = response.data.map((variable) => variable.name).filter((name): name is string => !!name);
        setVariables(variableNames);
      } else {
        // console.log("No variables found.");
        setVariables([]);
      }
    } catch (error) {
      console.error("Error while fetching variable list:", error);
    }
  };

  const fetchNamespaces = async () => {
    try {
      const response = await axiosWorkflowIntance.api.workflowServiceListNamespaces();
      const namespaces = response.data && response.data.namespaces ? response.data.namespaces : [];
      const namespaceNames = namespaces.map((namespace) => namespace?.namespaceInfo?.name).filter((name): name is string => !!name);

      if (namespaceNames.length === 0) {
        // console.log("No namespaces found.");
        setNamespacesData([]);
      } else {
        setNamespacesData(namespaceNames);
      }
    } catch (error) {
      console.error("Error fetching Namespaces:", error);
    }
  };

  const fetchPackages = async () => {
    try {
      let page = 1;
      const allPackages: Package[] = [];
  
      // Keep fetching until we get an empty or smaller-than-50 response
      while (true) {
        const response = await axiosRepoIntance.packages.listPackages(OWNER, {
          type: "container",
          page,
          limit: 50,
        });
        const data: Package[] = response.data || [];
  
        // Stop if no more results
        if (data.length === 0) {
          break;
        }
  
        // Accumulate these results
        allPackages.push(...data);
  
        // If we got fewer than 50, there are no more pages
        if (data.length < 50) {
          break;
        }
  
        page++;
      }
  
      if (allPackages.length > 0) {
        const temp1: Record<string, string> = {};
        const temp: Record<string, string[]> = allPackages.reduce(
          (acc: Record<string, string[]>, pkg: Package) => {
            if (pkg.name) {
              const version = pkg.version ? pkg.version : "N/A";
              const createdAt = pkg.created_at
                ? new Date(pkg.created_at).toLocaleString()
                : "N/A";
  
              if (!version.startsWith("sha256")) {
                const formattedValue = `${version}, ${createdAt}`;
                temp1[formattedValue] = version;
  
                if (!acc[pkg.name]) {
                  acc[pkg.name] = [];
                }
                acc[pkg.name].push(formattedValue);
              }
            }
            return acc;
          },
          {}
        );
  
        setNameToVersionMap(temp);
        setVersionMap(temp1);
      } else {
        // console.log("No packages found.");
        setNameToVersionMap({});
        setVersionMap({});
      }
    } catch (error) {
      console.error("Error fetching Packages:", error);
    }
  };
  

  const fetchDataFromDeploymentMainBranch = async () => {
    try {
      const response = await axiosRepoIntance.repos.repoGetContents(OWNER, "deployments", "deployment.json", { ref: "main" });
      const content = JSON.parse(base64ToUtf8(response.data.content || ""))
      console.log(`Fetched content from main branch of the deployments repo: `, content);
      setFormData((prevFormData) => ({
        ...prevFormData,
        resourceLimits: {
          cpu: extractNumericValue(content.resources?.limits?.cpu || "500m"),
          memory: extractNumericValue(content.resources?.limits?.memory || "512Mi"),
        },
        resourceRequest: {
          cpu: extractNumericValue(content.resources?.requests?.cpu || "250m"),
          memory: extractNumericValue(content.resources?.requests?.memory || "256Mi"),
        },
      }));
      return content;
    } catch (error) {
      console.error("Error fetching Contents of the deployment.json:", error);
    }
  };

  const fetchAndUpdateYml = async (secrets: string[], variables: string[]) => {
    try {
      const response = await axiosRepoIntance.repos.repoGetContents(
        OWNER,
        "deployments",
        ".gitea/workflows/cd_workflows.yml",
        { ref: "main" }
      );

      const workflow: Workflow = yaml.load(base64ToUtf8(response.data.content || "")) as Workflow;
      // console.log("workflow", workflow);

      const generateValuesStep = workflow.jobs.deploy.steps.find(
        (step) => step.name === "Generate values.yaml"
      );

      if (!generateValuesStep) {
        alert('Step "Generate values.yaml" not found.');
        return;
      }

      generateValuesStep.env = generateValuesStep.env || {};

      // console.log("Before adding secrets and variables:", generateValuesStep.env);

      secrets.forEach((secret) => {
        generateValuesStep.env![secret] = `\${{ secrets.${secret} }}`;
      });

      variables.forEach((variable) => {
        generateValuesStep.env![variable] = `\${{ variables.${variable} }}`;
      });

      const newYamlContent = yaml.dump(workflow);

      // console.log("Updated YAML Content:", newYamlContent);

      return newYamlContent;

    } catch (error) {
      console.error("Error fetching and updating YAML:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPackages();
      fetchNamespaces();
      listRepoActionSecrets();
      listRepoActionVariables();
      fetchDataFromDeploymentMainBranch();
    }
  }, [open]);

  useEffect(() => {
    const updateSchema = () => {
      if (nameToVersionMap) {
        const nameOptions = Object.keys(nameToVersionMap);

        const updatedSchema: JSONSchema7 = {
          ...schema,
          properties: {
            ...schema.properties,
            name: {
              ...(schema.properties?.name as JSONSchema7),
              type: "string",
              enum: nameOptions,
            },
            version: {
              ...(schema.properties?.version as JSONSchema7),
              type: "string",
              enum: formData.name ? nameToVersionMap[formData.name] || [] : [],
            },
            namespace: {
              ...(schema.properties?.namespace as JSONSchema7),
              type: "string",
              enum: namespacesData,
            },
            secrets: {
              ...(schema.properties?.secrets as JSONSchema7),
              type: "array",
              items: { type: "string", enum: secrets.length > 0 ? secrets : undefined },
              uniqueItems: true,
              default: [...initializedSecrets],
            },
            variables: {
              ...(schema.properties?.variables as JSONSchema7),
              type: "array",
              items: { type: "string", enum: variables.length > 0 ? variables : undefined },
              uniqueItems: true,
            },
          },
        };

        setSchema(updatedSchema);
      }
    };

    updateSchema();
  }, [nameToVersionMap, formData.name, namespacesData, secrets, variables]);

  const handleSubmit = async (data: any) => {
    setIsLoading(true);
    const formData = data.formData;
    const transformedData = transformFormData(formData);

    console.log("Transformed Data:", transformedData);

    try {
      const newBranchName = generateBranchName(transformedData);

      const updatedYmlContent = await fetchAndUpdateYml(transformedData.environment.secrets, transformedData.environment.variables);
      const ymlContentForPayload = updatedYmlContent || '';

      transformedData.environment.secrets = (transformedData.environment.secrets || []).filter((secret: string) => secret !== "KUBECONFIG_CONTENT");

      const payload = generatePayload(newBranchName, transformedData, ymlContentForPayload);

      // console.log("Payload generated", payload);

      const response = await axiosRepoIntance.repos.repoChangeFiles(OWNER, "deployments", payload);

      if (response.status === 201 || response.status === 200) {
        console.log("File updated successfully on branch:", newBranchName);
        setRefreshKey(prevKey => prevKey + 1);
      } else {
        console.error("Failed to update the file:", response);
      }
      onClose();
      setFormData({});
    } catch (error) {
      console.error("Error during branch creation or file update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const transformFormData = (formData: any) => {
    return {
      namespace: formData.namespace || "default",
      name: formData.name || "",
      version: versionMap[formData.version] || "",
      repoName: formData.name?.split("/")[0] || "",
      branchName: formData.name?.split("/")[1] || "",
      environment: {
        secrets: formData.secrets || [],
        variables: formData.variables || [],
      },
      resources: {
        limits: {
          cpu: formData.resourceLimits?.cpu ? `${formData.resourceLimits?.cpu}m` : "500m",
          memory: formData.resourceLimits?.memory
            ? `${formData.resourceLimits?.memory}Mi`
            : "512Mi",
        },
        requests: {
          cpu: formData.resourceRequest?.cpu ? `${formData.resourceRequest?.cpu}m` : "250m",
          memory: formData.resourceRequest?.memory
            ? `${formData.resourceRequest?.memory}Mi`
            : "256Mi",
        },
      },
    };
  };

  const generateBranchName = (data: any) => {
    return `${data.repoName}_${data.branchName}_${data.version}`;
  };

  const generatePayload = (newBranchName: string, data: any, ymlContent: string) => {
    return {
      branch: "main",
      new_branch: newBranchName,
      files: [
        {
          path: "deployment.json",
          content: utf8ToBase64(JSON.stringify(data, null, 2)),
          operation: "update" as "update",
        },
        {
          path: ".gitea/workflows/cd_workflows.yml",
          content: utf8ToBase64(ymlContent),
          operation: "update" as "update",
        },
      ],
      message: `Update deployment.json with new data on branch ${newBranchName} with the namespace: ${data.namespace}`,
    };
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, p: 2 }}>
        <Typography sx={{ m: 2 }} variant="h4">
          Add Deployment
        </Typography>
        <Form
          schema={schema}
          formData={formData}
          uiSchema={uiSchema}
          validator={validator}
          showErrorList={false}
          onChange={(e) => setFormData(e.formData)}
          onSubmit={handleSubmit}
        />
      </Box>
    </Drawer>
  );
};

export default AddDeployment;
